import { GAME_STATUSES, slugify, type Game, type GameStatus } from '@hive/shared';
import { Archive, ArchiveRestore } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/field';
import { useCreateGame, useGames, useUpdateGame } from '@/data/games';

/** Create, rename and archive games. Studio can be renamed but not archived. */
export function GamesPage() {
  const games = useGames();
  const create = useCreateGame();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const slug = slugify(name);

  function add() {
    if (!name.trim() || !slug) return;
    setError(null);
    create.mutate(
      { name: name.trim(), slug, status: 'idea' },
      {
        onSuccess: () => setName(''),
        onError: (e) =>
          setError(/duplicate|unique/i.test(e.message) ? `There's already a game at /grid/${slug}.` : e.message),
      },
    );
  }

  const list = games.data ?? [];
  return (
    <div className="mx-auto w-full max-w-[860px] px-7 pt-14 pb-10">
      <p className="hud-label mb-3.5">Grid</p>
      <h1 className="mb-6 font-display text-3xl font-black tracking-[0.04em] text-cyan uppercase text-shadow-glow-cyan">
        Games
      </h1>
      <form
        className="cut-tr mb-8 flex items-end gap-3 bg-bg-2/85 p-4 shadow-[inset_0_0_0_1px_var(--color-line)]"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <Field label="New game" className="flex-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="New game name" />
        </Field>
        <span className="pb-2 font-mono text-xs text-dim">{slug ? `/grid/${slug}` : ''}</span>
        <Button type="submit" disabled={!slug || create.isPending}>
          Create
        </Button>
      </form>
      {error ? (
        <p role="alert" className="mb-4 text-sm text-red">
          {error}
        </p>
      ) : null}
      {games.isError ? <p className="text-red">Can't load games: {games.error.message}</p> : null}
      <ul className="flex flex-col gap-2">
        {list.map((g) => (
          <GameRow key={g.id} game={g} />
        ))}
      </ul>
    </div>
  );
}

function GameRow({ game }: { game: Game }) {
  const update = useUpdateGame();
  const [name, setName] = useState(game.name);
  const archived = !!game.archived_at;
  return (
    <li
      className={`flex flex-wrap items-center gap-3 bg-bg-2/70 px-3 py-2 shadow-[inset_0_0_0_1px_var(--color-line)] ${archived ? 'opacity-60' : ''}`}
    >
      <Input
        aria-label={`Name of ${game.name}`}
        className="max-w-64"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (name.trim() && name.trim() !== game.name) update.mutate({ id: game.id, name: name.trim() });
          else setName(game.name);
        }}
      />
      <Select
        aria-label={`Status of ${game.name}`}
        className="w-auto"
        value={game.status}
        onChange={(e) => update.mutate({ id: game.id, status: e.target.value as GameStatus })}
      >
        {GAME_STATUSES.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
      <Link to={`/grid/${game.slug}`} className="font-mono text-xs text-cyan hover:underline">
        /grid/{game.slug}
      </Link>
      <span className="ml-auto">
        {game.is_studio ? (
          <span className="font-mono text-[11px] text-dim">Studio work; always here</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={update.isPending}
            onClick={() => update.mutate({ id: game.id, archived_at: archived ? null : new Date().toISOString() })}
          >
            {archived ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
            {archived ? 'Restore' : 'Archive'}
          </Button>
        )}
      </span>
      {update.error ? <p className="w-full text-xs text-red">{update.error.message}</p> : null}
    </li>
  );
}
